// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_ENDPOINTS } from '../config/apiConfig';
import { storageKey } from '../config/routes';

const AuthContext = createContext(null);

// Storage keys are namespaced per area (Separation contract v2.1 §3, §5.2):
// rw.kuestion.* and rw.suite.*. Both areas share one origin, so signing out of
// one leaves the other intact only because each reads its own keys.
// A user-experience separation, not a security boundary.
const LEGACY_SESSION_KEY = 'gw_user_session';
const LEGACY_SETTINGS_KEY = 'gw_user_settings';

// Default settings
const DEFAULT_SETTINGS = {
  sessionTimeoutMinutes: 15, // Default 15 minutes
};

// Min/Max timeout values (in minutes)
const MIN_TIMEOUT = 5;
const MAX_TIMEOUT = 180; // 3 hours

export const AuthProvider = ({ children, area = 'suite' }) => {
  const SESSION_KEY = storageKey(area, 'session');
  const SETTINGS_KEY = storageKey(area, 'settings');
  const TIMEOUT_FLAG = storageKey(area, 'session_timeout');
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [lastActivity, setLastActivity] = useState(Date.now());
  
  const timeoutRef = useRef(null);
  const activityListenerRef = useRef(null);
  // Counts callers that have asked the idle timeout to stand down. A candidate
  // can read a long exam question for well over the timeout without generating
  // a single mousedown/keydown, and exam answers live only in memory — so an
  // idle logout mid-sitting silently destroys a finished exam.
  const holdCountRef = useRef(0);

  // The session lives in sessionStorage (Preferences & storage contract §4,
  // build brief §14.2): it does not survive the tab, so a shared lab machine
  // never hands the next person a live session. Settings stay in localStorage.
  // Sessions written by the previous build into localStorage are carried
  // over once, so this deploy signs nobody out.
  // Load session and settings on mount
  useEffect(() => {
    if (area === 'kuestion') {
      try {
        const legacy = localStorage.getItem(LEGACY_SESSION_KEY);
        if (legacy && !sessionStorage.getItem(SESSION_KEY)) sessionStorage.setItem(SESSION_KEY, legacy);
        const legacySettings = localStorage.getItem(LEGACY_SETTINGS_KEY);
        if (legacySettings && !localStorage.getItem(SETTINGS_KEY)) localStorage.setItem(SETTINGS_KEY, legacySettings);
        localStorage.removeItem(LEGACY_SESSION_KEY);
        localStorage.removeItem(LEGACY_SETTINGS_KEY);
      } catch { /* private mode */ }
    }
    try {
      const carried = localStorage.getItem(SESSION_KEY);
      if (carried) {
        if (!sessionStorage.getItem(SESSION_KEY)) sessionStorage.setItem(SESSION_KEY, carried);
        localStorage.removeItem(SESSION_KEY);
      }
    } catch { /* private mode */ }
    const storedSession = sessionStorage.getItem(SESSION_KEY);
    const storedSettings = localStorage.getItem(SETTINGS_KEY);
    
    if (storedSession) {
      try {
        const parsed = JSON.parse(storedSession);
        // Check if session has expired
        const sessionTimestamp = parsed._sessionTimestamp || 0;
        const savedSettings = storedSettings ? JSON.parse(storedSettings) : DEFAULT_SETTINGS;
        const timeoutMs = (savedSettings.sessionTimeoutMinutes || 15) * 60 * 1000;
        
        if (Date.now() - sessionTimestamp < timeoutMs) {
          setUser(parsed);
          setSettings(savedSettings);
        } else {
          // Session expired
          sessionStorage.removeItem(SESSION_KEY);
        }
      } catch {
        sessionStorage.removeItem(SESSION_KEY);
      }
    }
    
    if (storedSettings) {
      try {
        setSettings(JSON.parse(storedSettings));
      } catch {
        localStorage.removeItem(SETTINGS_KEY);
      }
    }
    
    setIsLoading(false);
  }, [area, SESSION_KEY, SETTINGS_KEY]);

  // Activity tracking for session timeout
  useEffect(() => {
    if (!user) return;

    const updateActivity = () => {
      setLastActivity(Date.now());
      // Update session timestamp
      const updatedUser = { ...user, _sessionTimestamp: Date.now() };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(updatedUser));
    };

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    activityListenerRef.current = updateActivity;

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [user, SESSION_KEY]);

  // Session timeout checker
  useEffect(() => {
    if (!user) {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
      }
      return;
    }

    const checkTimeout = () => {
      // Something long-running and unattended (an exam sitting) is in progress.
      if (holdCountRef.current > 0) return;

      const timeoutMs = settings.sessionTimeoutMinutes * 60 * 1000;
      const timeSinceActivity = Date.now() - lastActivity;

      if (timeSinceActivity >= timeoutMs) {
        // Session timed out
        handleLogout(true);
      }
    };

    // Check every 30 seconds
    timeoutRef.current = setInterval(checkTimeout, 30000);

    return () => {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
      }
    };
  }, [user, settings.sessionTimeoutMinutes, lastActivity]);

  // Login function
  const login = useCallback(async (username, password) => {
    const response = await fetch(API_ENDPOINTS.LOGIN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json();

    if (!response.ok || data.status !== 'success') {
      const reason = data.reason || 'Login failed';
      throw new Error(reason);
    }

    // Store user data with timestamp. The login response carries the
    // Kuestion account mode alongside the user (Campus v3 §9.0); keep it with
    // the session so the app can route without an extra call. Absent means an
    // older server or token — the app then asks GET /campus/mode.
    const accountMode = data.accountMode ?? data.account_mode ?? data.user?.accountMode ?? data.user?.account_mode;
    const userData = { ...data.user, ...(accountMode !== undefined ? { accountMode } : {}), _sessionTimestamp: Date.now() };
    setUser(userData);
    setLastActivity(Date.now());
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(userData));

    return userData;
  }, [SESSION_KEY]);

  // Logout function
  const handleLogout = useCallback(async (isTimeout = false) => {
    try {
      // Send the token so the server can invalidate it; without it there is
      // nothing identifying the session being ended.
      const headers = { 'Content-Type': 'application/json' };
      if (user?.token) headers.Authorization = `Bearer ${user.token}`;
      await fetch(API_ENDPOINTS.LOGOUT, { method: 'POST', headers });
    } catch {
      // Ignore errors
    }

    setUser(null);
    sessionStorage.removeItem(SESSION_KEY);
    
    if (isTimeout) {
      // Store timeout flag for login page to show message
      sessionStorage.setItem(TIMEOUT_FLAG, 'true');
    }
  }, [user, SESSION_KEY, TIMEOUT_FLAG]);

  // Logout wrapper (for manual logout)
  const logout = useCallback(() => handleLogout(false), [handleLogout]);

  // Update settings
  const updateSettings = useCallback((newSettings) => {
    const updated = { ...settings, ...newSettings };
    // Enforce min/max
    if (updated.sessionTimeoutMinutes < MIN_TIMEOUT) {
      updated.sessionTimeoutMinutes = MIN_TIMEOUT;
    }
    if (updated.sessionTimeoutMinutes > MAX_TIMEOUT) {
      updated.sessionTimeoutMinutes = MAX_TIMEOUT;
    }
    setSettings(updated);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
  }, [settings, SETTINGS_KEY]);

  // Get remaining session time
  const getRemainingTime = useCallback(() => {
    if (!user) return 0;
    const timeoutMs = settings.sessionTimeoutMinutes * 60 * 1000;
    const elapsed = Date.now() - lastActivity;
    return Math.max(0, timeoutMs - elapsed);
  }, [user, settings.sessionTimeoutMinutes, lastActivity]);

  // Suspend the idle-logout while an unattended long task runs. Counted rather
  // than boolean so overlapping holders can't release each other's hold.
  // Always pair holdSession() with releaseSession() in a cleanup.
  // The server hands back a replacement token when a claim it carries has
  // just changed (PUT /campus/mode, on 200 and on the 409). Same user, same
  // TTL; only the token and the mode move.
  const refreshToken = useCallback((token, accountMode) => {
    if (!token) return;
    setUser((u) => {
      if (!u) return u;
      const next = { ...u, token, ...(accountMode ? { accountMode } : {}) };
      try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, [SESSION_KEY]);

  const holdSession = useCallback(() => {
    holdCountRef.current += 1;
  }, []);

  const releaseSession = useCallback(() => {
    holdCountRef.current = Math.max(0, holdCountRef.current - 1);
    // Treat the end of the held task as activity, so the user doesn't get
    // logged out the instant a long exam finishes.
    if (holdCountRef.current === 0) setLastActivity(Date.now());
  }, []);

  const isAuthenticated = Boolean(user);

  const value = {
    area,
    user,
    // The bearer token for user-scoped APIs. Backend nests it inside the user
    // object at login, and it is stored with the rest of the session.
    token: user?.token || null,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshToken,
    holdSession,
    releaseSession,
    settings,
    updateSettings,
    getRemainingTime,
    MIN_TIMEOUT,
    MAX_TIMEOUT,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
